import type { ApolloQueryResult } from '@apollo/client';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import type { IntlShape } from 'react-intl/src/types';

import { getLabel } from '@/utils/labelUtils';

import { CMSSection } from '@/cms/CMSSection';
import type { BreadcrumbItem } from '@/components/Breadcrumb';
import { Button4Gift } from '@/components/Button4Gift/Button4Gift';
import { OrSeparator } from '@/components/ORseparator/ORseparator';
import { Price } from '@/components/Price';
import { ProductDetail } from '@/components/ProductDetail';
import type { SecondaryCTAProps } from '@/components/ProductDetail/components';
import { ServicePerks } from '@/components/ServicePerks';
import { Visibility } from '@/components/Visibility';
import type {
  ContentfulPlaceholderProductDetailsFragment,
  ContentfulSelectorModalByMarketingPartnerQuery,
} from '@/graphql/contentful/api';
import type {
  OrderAppCtaType,
  OrderAppProductVariantBusinessDetailsFragment,
} from '@/graphql/orderApp/api';
import type {
  ProductsByMarketingPartnerQuery,
  ProductsBySkusQuery,
  ProductVariantWatchBasicDetailsFragment,
  ProductVariantWatchDetailsFragment,
} from '@/graphql/saleor/api';
import { useCheckout } from '@/hooks/useCheckout';
import { useGoogleAnalytics } from '@/hooks/useGoogleAnalytics';
import { useNativeCall } from '@/hooks/useNativeCall';
import { useRouter } from '@/hooks/useRouter';
import {
  filterEnabledStraps,
  sendAlgoliaEvent,
} from '@/integrations/algolia/helpers';
import { AlgoliaEventsNames } from '@/integrations/algolia/types';
import { getMultiselectValue } from '@/lib/attributes';
import { SALEOR_PRE_ORDER_CONFIG_KEY } from '@/lib/const';
import { captureError } from '@/lib/errors';
import { EVENT_NAME } from '@/lib/gtm/const';
import {
  getCollectionTranslatedName,
  getProductTranslatedName,
} from '@/lib/i18n';
import { paths } from '@/lib/paths';
import { getPreOrderConfig } from '@/lib/preOrder';
import { getProductPath, getProductType } from '@/lib/product';
import { fetchProductsBySkus } from '@/lib/product/api';
import { getGqlRegionFromLocale, getTaxMessageShort } from '@/lib/regions';
import { generateDataTestId } from '@/lib/tests';
import type { Maybe } from '@/lib/types';
import { commonMessages } from '@/messages/common';
import { useAlgoliaEvent } from '@/providers/AlgoliaEventProvider';
import { useCommonModalProviderContext } from '@/providers/CommonModalProvider';
import {
  isModuleActive,
  Modules,
  useConfigProvider,
} from '@/providers/ConfigProvider';
import { DraftModeContext } from '@/providers/DraftModeProvider/DraftModeContext';
import { useRegionProvider } from '@/providers/RegionProvider';

import { AvailabilityRequestModal } from './AvailabilityRequestModal';
import { BookAppointmentModal } from './BookAppointmentModal';
import { BoutiqueDeliveryInformation } from './BoutiqueDeliveryInformation/BoutiqueDeliveryInformation';
import { BoutiqueDeliveryModal } from './BoutiqueDeliveryModal/BoutiqueDeliveryModal';
import ChangeStrapButton from './ChangeStrapButton/ChangeStrapButton';
import ChangeStrapModal from './ChangeStrapModal/ChangeStrapModal';
import { FinanceCalculatorModal } from './FinanceCalculatorModal/FinanceCalculatorModal';
import { loadAffirm } from './loadAffirm';
import MarketingSelectorButton from './MarketingSelectorButton/MarketingSelectorButton';
import MarketingSelectorModal from './MarketingSelectorModal/MarketingSelectorModal';
import { OneyModal } from './OneyModal/OneyModal';
import { PreOrderInformation } from './PreOrderInformation/PreOrderInformation';
import { SendGiftModal } from './SendGiftModal/SendGiftModal';

import * as S from './ProductDetailSection.styles';

export interface ProductDetailSectionOpts {
  businessData: Maybe<OrderAppProductVariantBusinessDetailsFragment>;
  marketingPartnerProductsResult: ApolloQueryResult<ProductsByMarketingPartnerQuery>;
  marketingSelectorModalResult:
    | NonNullable<
        ContentfulSelectorModalByMarketingPartnerQuery['selectorCollection']
      >['items'][0]
    | null;
  variantBasicDetails: ProductVariantWatchBasicDetailsFragment;
  variantDetails: Maybe<ProductVariantWatchDetailsFragment>;
}
export const highlightCollection = (
  collectionName: string,
  productNameWithCollection: string,
) => {
  const normalizedCollectionName = collectionName
    .replace(/\s+/g, '')
    .toLowerCase();
  const normalizedProductNameWithCollection = productNameWithCollection
    .replace(/\s+/g, '')
    .toLowerCase();

  const index = normalizedProductNameWithCollection.indexOf(
    normalizedCollectionName,
  );

  if (index !== -1) {
    const beforeMatch = productNameWithCollection.slice(0, index);
    const match = productNameWithCollection.slice(
      index,
      index + collectionName.length + 1,
    );
    const afterMatch = productNameWithCollection.slice(
      index + collectionName.length + 1,
    );

    return (
      <>
        {beforeMatch}
        <ProductDetail.PromotedName data-test-id='ProductDetailPromotedName'>
          {match}
        </ProductDetail.PromotedName>
        {afterMatch}
      </>
    );
  } else {
    return productNameWithCollection;
  }
};
export interface BundleVariationOption {
  alt: string | null | undefined;
  coords: { x1: number; x2: number; y1: number; y2: number };
  image: string | undefined;
  slug: string | null | undefined;
  strapColor: string | null | undefined;
  strapColorCode: string | null | undefined;
  strapMaterial: string | null | undefined;
}

export type ProductDetailSectionProps =
  ContentfulPlaceholderProductDetailsFragment & ProductDetailSectionOpts;

export const ProductDetailSection = ({
  businessData,
  marketingPartnerProductsResult,
  marketingSelectorModalResult,
  perksCollection,
  variantBasicDetails: { id, metadata, product, sku },
  variantDetails,
}: ProductDetailSectionProps) => {
  const { country, locale, productCatalogMode } = useRegionProvider();

  const { locale: routerLocale } = useRouter();
  const { channel } = getGqlRegionFromLocale(routerLocale);
  const taxMessage = getTaxMessageShort(country.channelSlug);
  const { moduleActivations } = useConfigProvider();
  const isBoutiquePickUpAvailable = isModuleActive(
    moduleActivations,
    Modules.BOUTIQUE_PICKUP,
  );
  const is4GiftBtnAvailable = isModuleActive(
    moduleActivations,
    Modules.FOR_GIFT,
  );

  const isAffirmModuleAvailable = isModuleActive(
    moduleActivations,
    Modules.AFFIRM,
  );
  const isCembraMoudleAvailable = isModuleActive(
    moduleActivations,
    Modules.PDP_CEMBRA_FINANCING,
  );
  const isOneyModuleAvailable = isModuleActive(
    moduleActivations,
    Modules.PDP_ONEY_3X,
  );
  const isAgosModuleAvailable = isModuleActive(
    moduleActivations,
    Modules.PDP_AGOS,
  );
  const isCreditPlusAvailable = isModuleActive(
    moduleActivations,
    Modules.PDP_CREDIT_PLUS,
  );
  const isZipModuleAvailable = isModuleActive(moduleActivations, Modules.ZIP);

  const isAvailabilityRequestAvailable =
    isModuleActive(moduleActivations, Modules.AVAILABILITY_REQUEST) &&
    productCatalogMode;

  const financeCalculatorPayment = Object.entries({
    agos: isAgosModuleAvailable,
    cembra: isCembraMoudleAvailable,
    creditPlus: isCreditPlusAvailable,
  }).find(([, value]) => value)?.[0];

  const isNotifyMe: boolean = getPreOrderConfig(
    metadata,
    SALEOR_PRE_ORDER_CONFIG_KEY.NotifyMe,
    country.code,
  );

  const [isBookAppointmentModalOpen, setIsBookAppointmentModalOpen] =
    useState(false);
  const [isChangeStrapModalOpen, setIsChangeStrapModalOpen] = useState(false);
  const [isMarketingSelectorModalOpen, setIsMarketingSelectorModalOpen] =
    useState(false);
  const [isBoutiqueDeliveryModalOpen, setIsBoutiqueDeliveryModalOpen] =
    useState(false);
  const [is4GiftsAvailable, setIs4GiftsAvailable] = useState(false);
  const [isFinanceCalculatorOpen, setIsFinanceCalculatorOpen] = useState(false);
  const [isOneyModalOpen, setIsOneyModalOpen] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [enabledStrapSkus, setEnabledStrapSkus] = useState<string[]>([]);
  const [isAvailabilityRequestModalOpen, setIsAvailabilityRequestModalOpen] =
    useState(false);
  const [variantCTA, setVariantCTA] = useState<OrderAppCtaType>('NO_CTA');
  const { openNotifyMe, setContactUsModalStatus } =
    useCommonModalProviderContext();
  const {
    lineAdd,
    snapshot: { isProcessing },
  } = useCheckout();

  const { initiateNativeCall } = useNativeCall(country.code);
  const { formatMessage } = useIntl();

  const { sendEcommerceEventBySkus, sendEvent } = useGoogleAnalytics();

  const { indexName, isSearched, objectIDs, queryID, setObjectIDs } =
    useAlgoliaEvent();

  useEffect(() => {
    if (businessData?.variantCTA) {
      setVariantCTA(businessData.variantCTA);
    }
  }, [businessData?.variantCTA]);

  const collection = product?.collections?.[0];
  let collectionName = getCollectionTranslatedName(product);
  collectionName = collectionName ? collectionName.trim() : '';
  const name = getProductTranslatedName(product);
  const productNameWithCollection = !name?.includes(collectionName?.trim())
    ? (collectionName + ' ' + name).trim()
    : name;

  const productType = getProductType(product.productType);
  const isCallToBuyAvailable = Boolean(
    businessData?.phoneCTA === 'ORDER' &&
      variantDetails?.quantityAvailable &&
      variantDetails?.quantityAvailable > 0,
  );

  const isCallToPreOrderAvailable = Boolean(
    businessData?.phoneCTA === 'PRE_ORDER' &&
      variantDetails?.quantityAvailable &&
      variantDetails?.quantityAvailable <= 0,
  );

  const getDistributionBCode = () => {
    const firstValue = product?.distributionStatus?.values?.[0];
    const externalRef = firstValue?.externalReference;
    if (externalRef && externalRef.includes('-')) {
      return externalRef.split('-')[1];
    }
    return null;
  };

  const distributionBCode = getDistributionBCode();

  // BEC-19123: Display "Notify me" CTA for two specific cases:
  // CASE A: Sellable WEB = YES, Stock = NO, Distribution status = B2
  // CASE B: Sellable WEB = YES, Stock = NO, Distribution status = B4
  // Additional business rules: isNotifyMe master switch must be enabled, not in catalog mode
  const isSecondaryNotifyMeAvailable = Boolean(
    isNotifyMe &&
      (businessData as any)?.webSellable === true &&
      (businessData?.quantityAvailable === 0 ||
        !businessData?.quantityAvailable) &&
      (distributionBCode === 'B2' || distributionBCode === 'B4') &&
      !productCatalogMode,
  );

  let breadcrumbs: BreadcrumbItem[] = [];
  breadcrumbs = getBreadcrumbs(
    formatMessage,
    collectionName,
    collection!.slug,
    name,
  );

  const SECONDARY_CTA_DATA: SecondaryCTAProps['items'] = [
    {
      icon: 'calendar',
      message: <FormattedMessage {...commonMessages.bookAppointment} />,
      onClick: (): void => {
        sendEvent({
          event: EVENT_NAME.INTENTION_BOOK_APPT,
        });
        setIsBookAppointmentModalOpen(true);
      },
    },
  ];

  const callToBuy = () => {
    if (sku) {
      sendEcommerceEventBySkus({
        event: EVENT_NAME.CALL_TO_BUY,
        skus: [sku],
      });
    }
    initiateNativeCall();
  };

  if (isSecondaryNotifyMeAvailable) {
    SECONDARY_CTA_DATA.push({
      icon: 'mail',
      message: <FormattedMessage {...commonMessages.notifyMe} />,
      onClick: (): void => {
        if (sku) openNotifyMe({ sku });
      },
    });
  }

  if (isCallToBuyAvailable && !productCatalogMode)
    SECONDARY_CTA_DATA.unshift({
      icon: 'telephoneCall',
      message: <FormattedMessage {...commonMessages.callToBuy} />,
      // eslint-disable-next-line @typescript-eslint/no-empty-function
      onClick: () => callToBuy(),
    });

  if (isCallToPreOrderAvailable && !productCatalogMode)
    SECONDARY_CTA_DATA.unshift({
      icon: 'telephoneCall',
      message: <FormattedMessage {...commonMessages.callToPreOrder} />,
      // eslint-disable-next-line @typescript-eslint/no-empty-function
      onClick: () => {},
    });

  if (isAvailabilityRequestAvailable) {
    SECONDARY_CTA_DATA.push({
      icon: 'mail',
      message: <FormattedMessage {...commonMessages.availabilityRequest} />,
      onClick: (): void => {
        setIsAvailabilityRequestModalOpen(true);
      },
    });
  }

  const handleAddToCart = async () => {
    if (variantCTA === 'ORDER' || variantCTA === 'PRE_ORDER') {
      await lineAdd(id, variantCTA);
    } else if (variantCTA === 'NOTIFY') {
      if (sku) openNotifyMe({ sku });
    } else {
      setContactUsModalStatus(true, sku);
    }
    if (isSearched) {
      if (sku) {
        setObjectIDs([...objectIDs, sku]);
      }
      sendAlgoliaEvent({
        event: AlgoliaEventsNames.addedToCartObjectIDsAfterSearch,
        objectIDs: sku ? [sku] : [],
        queryID: queryID,
        indexName: indexName,
        value: variantDetails?.pricing?.price?.gross.amount,
        currency: variantDetails?.pricing?.price?.gross.currency,
      });
    }
  };

  const check4Gift = useCallback(async () => {
    try {
      const res = await fetch(
        `${process.env.NEXT_PUBLIC_4GIFT_URL_ENDPOINT}/api/${process.env.NEXT_PUBLIC_4GIFT_URL_VERSION}/breitling/products-4gift-availability?skus[]=${sku}&channel=${channel}`,
      );
      const data = await res.json();
      if (data?.availabilities && data.availabilities.length > 0 && sku) {
        setIs4GiftsAvailable(data.availabilities[0][sku]);
      }
    } catch (err) {
      captureError(err as Error);
      setIs4GiftsAvailable(false);
    }
  }, [sku, channel]);

  useEffect(() => {
    if (variantCTA === 'ORDER' && is4GiftBtnAvailable) {
      check4Gift();
    }
  }, [variantCTA, is4GiftBtnAvailable]);

  const marketingStatus = getMultiselectValue(
    variantDetails?.product.marketingStatus,
    false,
  );

  const marketingPartner = getMultiselectValue(
    variantDetails?.product.marketingPartner,
    false,
  );

  // we need to have a marketing partner and more than one marketing partner product to show the modal, otherwise there are no other products to switch to
  const shouldShowMarketingSelectorModal =
    marketingPartner?.length > 0 &&
    marketingPartnerProductsResult?.data?.products?.edges &&
    marketingPartnerProductsResult.data.products.edges.length > 1 &&
    marketingSelectorModalResult;

  const launchDate = variantDetails?.product?.launchDate?.values?.[0]
    ?.name as string;

  const label = getLabel(marketingStatus, launchDate, true);

  const { query } = useRegionProvider();

  const bundleVariations = useMemo(
    () => getMultiselectValue(variantDetails?.product?.bundleVariations),
    [variantDetails?.product?.bundleVariations],
  );
  const draftMode = useContext(DraftModeContext);
  useEffect(() => {
    filterEnabledStraps(country, locale.code, bundleVariations, draftMode).then(
      (skus) => {
        setEnabledStrapSkus(skus);
      },
    );
  }, [bundleVariations]);

  const [data, setData] = useState<ProductsBySkusQuery | null>(null);
  useEffect(() => {
    const fetchData = async () => {
      const result = await fetchProductsBySkus({
        skus: enabledStrapSkus,
        languageCode: query.languageCode,
      });
      if (result) {
        setData(result);
      }
    };

    fetchData();
  }, [enabledStrapSkus, query.languageCode]);

  let strapPreviewCoords = { x1: 415, x2: 800, y1: 840, y2: 900 };
  const coordsString = product.media?.[0]?.strapPreviewCoordinates;
  if (coordsString && coordsString !== '') {
    strapPreviewCoords = JSON.parse(coordsString);
  }

  const bundleVariationOptions: BundleVariationOption[] = [
    {
      coords: strapPreviewCoords,
      image: (product?.media?.[0]?.url ?? '').replace('_512.', '_4096.'),
      strapColor: product.strapColor?.values[0]?.translation?.plainText,
      strapColorCode: product.strapColor?.values[0]?.translation?.name,
      strapMaterial: product.strapMaterial?.values[0]?.translation?.plainText,
      slug: product.slug,
      alt: name,
    },
  ];

  data?.products?.edges?.map((value) => {
    bundleVariationOptions.push({
      coords: strapPreviewCoords,
      image: value.node.thumbnail?.url,
      strapColor: value.node.strapColor?.values[0]?.translation?.plainText,
      strapColorCode: value.node.strapColor?.values[0]?.translation?.name,
      strapMaterial:
        value.node.strapMaterial?.values[0]?.translation?.plainText,
      alt: value.node.thumbnail?.alt,
      slug: value.node.slug,
    });
  });

  const useAffirm = process.env.NEXT_PUBLIC_USE_AFFIRM === 'true';

  useEffect(() => {
    if (isAffirmModuleAvailable) {
      let affirmKey, affirmUrl;

      switch (country.code.toLowerCase()) {
        case 'ca':
          affirmKey = process.env.NEXT_PUBLIC_AFFIRM_KEY_CA ?? '';
          affirmUrl = process.env.NEXT_PUBLIC_AFFIRM_URL_CA ?? '';
          break;
        default:
          affirmKey = process.env.NEXT_PUBLIC_AFFIRM_KEY_US ?? '';
          affirmUrl = process.env.NEXT_PUBLIC_AFFIRM_URL_US ?? '';
          break;
      }
      loadAffirm(affirmKey, affirmUrl);
    }
  }, [
    locale,
    useAffirm,
    variantDetails?.pricing?.price?.gross?.amount,
    isAffirmModuleAvailable,
    isZipModuleAvailable,
    variantCTA,
  ]);

  const shareableProductData = {
    sku: product.defaultVariant?.sku,
    name: product.name,
    collection: product.collections?.[0]?.name,
    image: product.media?.[0]?.url,
    url: [
      process.env.NEXT_PUBLIC_URL,
      country.code.toLowerCase(),
      '-',
      locale.slug,
      getProductPath(product, true),
    ].join(''),
  };

  useEffect(() => {
    if (sku) {
      sendEcommerceEventBySkus({
        event: EVENT_NAME.VIEW_ITEM,
        skus: [sku],
      });
    }
  }, [sku]);

  const defaultImageAlt = `${product.name} - ${sku}`;
  const shouldShowBoutiquePickup =
    isBoutiquePickUpAvailable &&
    !productCatalogMode &&
    ['ORDER', 'PRE_ORDER'].includes(variantCTA);

  return (
    <CMSSection
      {...generateDataTestId('productDetail', 'section')}
      data-test-id='ProductDetailSection'
    >
      <ProductDetail>
        <ProductDetail.ImagesGallery
          alt={defaultImageAlt}
          data-test-id='ProductDetailImagesGallery'
          gallery='watchPdpCarousel'
          media={product.media}
          mediaLarge={product.mediaLarge}
          name={name}
          price={variantDetails?.pricing?.price?.gross.amount}
          productType={productType}
          shareableProductData={shareableProductData}
          sku={sku}
          tag={label && <FormattedMessage {...label} />}
          theCurrency={variantDetails?.pricing?.price?.gross.currency}
        >
          <ProductDetail.Breadcrumbs
            data-test-id='ProductDetailBreadcrumbs'
            items={breadcrumbs}
            mdHidden
          />
          <Visibility mdHidden>
            <S.ButtonWrapper>
              <div style={{ display: 'inline-flex', flexDirection: 'row' }}>
                {enabledStrapSkus?.length > 0 && (
                  <ChangeStrapButton
                    coords={strapPreviewCoords}
                    data-testid='ChangeStrapButton'
                    name={name}
                    setIsChangeStrapModalOpen={setIsChangeStrapModalOpen}
                    url={product.media?.[0]?.url ?? ''}
                  />
                )}
                {shouldShowMarketingSelectorModal && (
                  <div style={{ marginLeft: '1em' }}>
                    <MarketingSelectorButton
                      data-testid='MarketingSelectorButton'
                      marketingPartnerProductsResult={
                        marketingPartnerProductsResult
                      }
                      marketingSelectorModalResult={
                        marketingSelectorModalResult
                      }
                      setIsMarketingSelectorModalOpen={
                        setIsMarketingSelectorModalOpen
                      }
                      sku={sku}
                    />
                  </div>
                )}
              </div>
            </S.ButtonWrapper>
          </Visibility>
        </ProductDetail.ImagesGallery>

        <ProductDetail.DetailsCard data-test-id='ProductDetailDetailsCard'>
          <ProductDetail.Breadcrumbs
            data-test-id='ProductDetailBreadcrumbs'
            items={breadcrumbs}
            mdVisible
          />

          <ProductDetail.Sku data-test-id='ProductDetailSku'>
            {sku}
          </ProductDetail.Sku>
          <ProductDetail.Name data-test-id='ProductDetailName'>
            {highlightCollection(collectionName, productNameWithCollection)}
          </ProductDetail.Name>
          {variantDetails?.pricing?.price?.gross && (
            <ProductDetail.Pricing data-test-id='ProductDetailPricing'>
              <Price price={variantDetails?.pricing?.price?.gross} size='xl' />
              <ProductDetail.PriceInformation
                data-test-id='ProductDetailPriceInformation'
                loading={!variantDetails}
              />

              <ProductDetail.TaxLabel data-test-id='ProductDetailTaxLabel'>
                {taxMessage && formatMessage(taxMessage)}
              </ProductDetail.TaxLabel>
            </ProductDetail.Pricing>
          )}
          {!productCatalogMode && (
            <>
              {useAffirm && isAffirmModuleAvailable && (
                <ProductDetail.AffirmSentence
                  amount={
                    (variantDetails?.pricing?.price?.gross?.amount ?? 0) * 100
                  }
                  className='affirm-as-low-as'
                  data-page-type='product'
                  data-test-id='ProductDetailAffirmSentence'
                  variantCTA={variantCTA}
                ></ProductDetail.AffirmSentence>
              )}
              {isZipModuleAvailable && (
                <ProductDetail.ZipWidget
                  countryCode={country.code}
                  price={variantDetails?.pricing?.price?.gross ?? null}
                  variantCTA={variantCTA}
                />
              )}
              {isCembraMoudleAvailable && (
                <ProductDetail.CembraSentence
                  onLearnMore={() => setIsFinanceCalculatorOpen(true)}
                  price={variantDetails?.pricing?.price?.gross ?? null}
                  variantCTA={variantCTA}
                />
              )}
              {isOneyModuleAvailable && (
                <ProductDetail.OneySentence
                  onOneyClick={() => setIsOneyModalOpen(true)}
                  price={variantDetails?.pricing?.price?.gross ?? null}
                  variantCTA={variantCTA}
                />
              )}
              {isAgosModuleAvailable && (
                <ProductDetail.AgosSentence
                  onCalculateFinancing={() => setIsFinanceCalculatorOpen(true)}
                  price={variantDetails?.pricing?.price?.gross ?? null}
                  variantCTA={variantCTA}
                />
              )}
              {isCreditPlusAvailable && (
                <ProductDetail.CreditPlusSentence
                  onLearnMore={() => setIsFinanceCalculatorOpen(true)}
                  price={variantDetails?.pricing?.price?.gross ?? null}
                  variantCTA={variantCTA}
                />
              )}
              <ProductDetail.V12Sentence
                amount={variantDetails?.pricing?.price?.gross?.amount ?? 0}
                country={country.code}
                currency={variantDetails?.pricing?.price?.gross.currency ?? ''}
                isLoading={!businessData}
                isProcessing={isProcessing}
                onAddToCart={handleAddToCart}
                variantCTA={variantCTA}
              />
              <ProductDetail.OricoFinancing
                amount={variantDetails?.pricing?.price?.gross?.amount ?? 0}
                country={country.code}
                variantCTA={variantCTA}
              />
            </>
          )}
          <div style={{ display: 'inline-flex', flexDirection: 'row' }}>
            {enabledStrapSkus?.length > 0 && (
              <div style={{ marginRight: '1em' }}>
                <Visibility mdVisible>
                  <ChangeStrapButton
                    coords={strapPreviewCoords}
                    data-testid='ChangeStrapButton'
                    name={name}
                    setIsChangeStrapModalOpen={setIsChangeStrapModalOpen}
                    url={product.media?.[0]?.url ?? ''}
                  />
                </Visibility>
              </div>
            )}

            {shouldShowMarketingSelectorModal && (
              <Visibility mdVisible>
                <MarketingSelectorButton
                  data-testid='MarketingSelectorButton'
                  marketingPartnerProductsResult={
                    marketingPartnerProductsResult
                  }
                  marketingSelectorModalResult={marketingSelectorModalResult}
                  setIsMarketingSelectorModalOpen={
                    setIsMarketingSelectorModalOpen
                  }
                  sku={sku}
                />
              </Visibility>
            )}
          </div>

          {shouldShowBoutiquePickup && (
            <ProductDetail.BoutiquePickUp data-test-id='BoutiquePickupWrapper'>
              {formatMessage(commonMessages.pdpBoutiqueDeliveryAvailableLabel)}
              <BoutiqueDeliveryInformation
                loading={!variantDetails}
                onClick={() => setIsBoutiqueDeliveryModalOpen(true)}
              />
            </ProductDetail.BoutiquePickUp>
          )}

          {!productCatalogMode && variantDetails?.pricing && (
            <ProductDetail.CTA
              cta={variantCTA}
              data-test-id='ProductDetailCTA'
              isProcessing={isProcessing}
              loading={!businessData}
              onClick={handleAddToCart}
            />
          )}

          {is4GiftsAvailable && variantCTA === 'ORDER' && (
            <>
              <OrSeparator />
              <Button4Gift onClick={() => setIsModalOpen(true)} />
            </>
          )}
          {variantCTA === 'PRE_ORDER' && <PreOrderInformation />}
          <ProductDetail.SecondaryCTA
            data-test-id='ProductDetailSecondaryCTA'
            items={SECONDARY_CTA_DATA}
          />
          <BookAppointmentModal
            data-test-id='BookAppointmentModal'
            isOpen={isBookAppointmentModalOpen}
            name={name}
            onClose={() => {
              setIsBookAppointmentModalOpen(false);
            }}
            product={product}
            sku={sku}
            variantDetails={variantDetails}
          />
          <ChangeStrapModal
            bundleVariationOptions={bundleVariationOptions}
            close={() => setIsChangeStrapModalOpen(false)}
            data-test-id='ChangeStrapModal'
            isOpen={isChangeStrapModalOpen}
            onClose={() => {
              setIsChangeStrapModalOpen(false);
            }}
          />
          <MarketingSelectorModal
            close={() => setIsMarketingSelectorModalOpen(false)}
            data-test-id='MarketingSelectorModal'
            isOpen={isMarketingSelectorModalOpen}
            marketingPartnerProductsResult={marketingPartnerProductsResult}
            marketingSelectorModalResult={marketingSelectorModalResult}
            onClose={() => setIsMarketingSelectorModalOpen(false)}
          />
          {!productCatalogMode && (
            <>
              <ServicePerks
                data-test-id='ServicePerksDesktop'
                mdVisible
                perksCollection={perksCollection}
                variant='desktop'
              />
              <ServicePerks
                data-test-id='ServicePerksMobile'
                mdHidden
                perksCollection={perksCollection}
                variant='mobile'
              />
            </>
          )}
        </ProductDetail.DetailsCard>
        <SendGiftModal
          country={country.code}
          isOpen={isModalOpen}
          locale={locale.slug}
          onClose={() => setIsModalOpen(false)}
          sku={sku}
        />
        <BoutiqueDeliveryModal
          isOpen={isBoutiqueDeliveryModalOpen}
          onClose={() => {
            setIsBoutiqueDeliveryModalOpen(false);
          }}
        >
          {formatMessage(commonMessages.pdpBoutiqueDeliveryAvailableModal)}
        </BoutiqueDeliveryModal>
        {financeCalculatorPayment && (
          <FinanceCalculatorModal
            handleAddToCart={handleAddToCart}
            isOpen={isFinanceCalculatorOpen}
            isProcessing={isProcessing}
            loading={!businessData}
            onClose={() => setIsFinanceCalculatorOpen(false)}
            paymentType={financeCalculatorPayment as any}
            price={variantDetails?.pricing?.price?.gross ?? null}
            variantCTA={variantCTA}
          />
        )}
        <OneyModal
          isOpen={isOneyModalOpen}
          onClose={() => setIsOneyModalOpen(false)}
          price={variantDetails?.pricing?.price?.gross ?? null}
        />
        <AvailabilityRequestModal
          data-test-id='availabilityRequestModal'
          isOpen={isAvailabilityRequestModalOpen}
          onClose={() => {
            setIsAvailabilityRequestModalOpen(false);
          }}
          price={variantDetails?.pricing?.price?.gross ?? null}
          product={product}
        />
      </ProductDetail>
    </CMSSection>
  );
};

const getBreadcrumbs = (
  formatMessage: IntlShape['formatMessage'],
  collectionName: string,
  collectionSlug: string,
  watchName?: string,
): BreadcrumbItem[] => {
  const getCollectionTarget = (): string => {
    if (!watchName) {
      return paths.watches.collection.asPath({ slug: collectionSlug });
    }

    switch (true) {
      case watchName.includes('NFL'):
        return paths.watches.nfl.asPath();
      default:
        return paths.watches.collection.asPath({ slug: collectionSlug });
    }
  };

  const getCollectionLabel = (): string => {
    if (!watchName) {
      return collectionName;
    }

    switch (true) {
      case watchName.includes('NFL'):
        return 'NFL';
      default:
        return collectionName;
    }
  };

  return [
    { label: formatMessage(commonMessages.home), target: paths.url() },
    {
      label: formatMessage(commonMessages.watches),
      target: paths.watches.url(),
    },
    {
      label: getCollectionLabel(),
      target: getCollectionTarget(),
    },
  ];
};
